import { useApp, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, TCommonDataObj, TWidgetProps } from '@libs/types';
import SumsubWebSdk from '@sumsub/websdk-react';
import { AnyEventName, EventPayload, SnsError } from '@sumsub/websdk/types/types';
import React from 'react';
import { TSumsub } from '../../types/data';
import { getInlineStyles } from '../../utils/styles';

interface IdCheckOnApplicantStatusChangedPayload {
    reviewStatus: string;
    reviewResult: {
        reviewAnswer: string;
        reviewRejectType: string;
    };
}

export const Sumsub: React.FC<TWidgetProps<TSumsub>> = ({ widget }) => {
    const { language } = useApp();
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerActionsByNameAndType } = useTriggerAction(widget);

    const expirationHandler = async (): Promise<string> => {
        try {
            const newWidget = await triggerActionsByNameAndType(ActionActionTypeEnum.onRefresh, 'refreshToken');
            return newWidget?.data?.accessToken ?? '';
        } catch (error) {
            console.error('Error fetching new access token:', error);
            return '';
        }
    };

    const onMessage = (type: AnyEventName, _payload: EventPayload<AnyEventName>) => {
        if (type !== 'idCheck.onApplicantStatusChanged') return;

        const { reviewResult: result, reviewStatus: status } = _payload as IdCheckOnApplicantStatusChangedPayload;

        if (status === 'completed' && result) {
            const { reviewAnswer, reviewRejectType } = result;
            if ((reviewAnswer === 'RED' && reviewRejectType === 'FINAL') || reviewAnswer === 'GREEN') {
                triggerActionsByNameAndType(ActionActionTypeEnum.onFilled, 'onSuccess').then();
            }
        }
    };

    const onError = (error: SnsError) => {
        updateWidgetValue(error as unknown as TCommonDataObj);
        triggerActionsByNameAndType(ActionActionTypeEnum.onFilled, 'onSuccess').then();
    };

    return (
        <div data-test={widget.uid} style={getInlineStyles({ margin: widget.options?.margin })}>
            <SumsubWebSdk
                accessToken={widget.data?.accessToken ?? ''}
                config={{ lang: language }}
                expirationHandler={expirationHandler}
                onError={onError}
                onMessage={onMessage}
            />
        </div>
    );
};
